/**
 * bot-settings.ts — Kiểu + mặc định + hàm thuần cho màn cấu hình nhân viên AI.
 * Khớp backend `ecdp_backend_source/src/modules/ai/bot-settings.ts` (backend là nơi kiểm chặt).
 */
import { i18n, currentLocale } from '@/lib/i18n'

export type OutputLanguage = 'auto' | 'vi' | 'en' | 'zh' | 'ja' | 'ko' | 'th' | 'id' | 'km' | 'lo' | 'fr' | 'de' | 'es' | 'ru'
export const OUTPUT_LANGUAGES: Array<{ value: OutputLanguage; label: string }> = [
  { value: 'auto', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.auto') } },
  { value: 'vi', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.vi') } },
  { value: 'en', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.en') } },
  { value: 'zh', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.zh') } },
  { value: 'ja', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.ja') } },
  { value: 'ko', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.ko') } },
  { value: 'th', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.th') } },
  { value: 'id', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.id') } },
  { value: 'km', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.km') } },
  { value: 'lo', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.lo') } },
  { value: 'fr', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.fr') } },
  { value: 'de', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.de') } },
  { value: 'es', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.es') } },
  { value: 'ru', get label() { return i18n.t('aiTrainLib:botSettings.outputLanguage.ru') } },
]

export type TimeWindow = { days: number[]; start: string; end: string }
export type HandoffTimeWindow = TimeWindow
export type HandoffIntent = { id: string; name: string; description: string; enabled: boolean }
export type ConditionOp = '=' | '!=' | 'in' | 'not_in' | 'is_null' | 'is_not_null' | '>' | '<' | '>=' | '<='
/** Điều kiện trên biến của danh mục biến, vd { variable: 'system.ai_reply_rounds', op: '>=', value: 20 }. */
export type Condition = { variable: string; op: ConditionOp; value?: string | number | string[] }
export const OP_LABELS: Record<ConditionOp, string> = {
  get '='() { return i18n.t('aiTrainLib:botSettings.op.eq') },
  get '!='() { return i18n.t('aiTrainLib:botSettings.op.neq') },
  get in() { return i18n.t('aiTrainLib:botSettings.op.in') },
  get not_in() { return i18n.t('aiTrainLib:botSettings.op.notIn') },
  get is_null() { return i18n.t('aiTrainLib:botSettings.op.isNull') },
  get is_not_null() { return i18n.t('aiTrainLib:botSettings.op.isNotNull') },
  get '>'() { return i18n.t('aiTrainLib:botSettings.op.gt') },
  get '<'() { return i18n.t('aiTrainLib:botSettings.op.lt') },
  get '>='() { return i18n.t('aiTrainLib:botSettings.op.gte') },
  get '<='() { return i18n.t('aiTrainLib:botSettings.op.lte') },
}
/** Khung thời gian dừng (Tự động ngừng trả lời) — khớp backend AutoStopSchedule. */
export type AutoStopSchedule = {
  repeat: 'weekly' | 'daily' | 'dates'
  /** 0 = CN … 6 = T7 (khi weekly). */
  days: number[]
  dateFrom?: string
  dateTo?: string
  timezone: string
  start: string
  end: string
}
export type AutoStopSettings = {
  enabled: boolean
  conditionGroups: Array<{ conditions: Condition[] }>
  schedules: AutoStopSchedule[]
  closingMessage: string
  closingImages: string[]
}
export type CollectAttribute = {
  key: string
  name: string
  description: string
  type: 'text' | 'select' | 'number'
  options?: string[]
  scope: 'contact' | 'conversation'
  syncField?: string | null
}
/** Trả lời nhóm: mọi tin / khi được gọi. ('inherit' cũ — theo cài đặt chung — đã bỏ; đọc vào thành 'all'.) */
export type GroupReplyMode = 'all' | 'trigger'

export type BotSettings = {
  outputLanguage: OutputLanguage
  fastMode: boolean
  unknownReply: string
  retrievalThreshold: number | null
  rewriteQuery: boolean
  /** Đợi khách nhắn xong (giây, 0..120) rồi mới trả lời — gộp các tin nhắn liên tiếp. */
  debounceSeconds: number
  /** Kiểm tra lại câu trả lời trước khi gửi (thêm một lượt AI rà soát). */
  verifyBeforeSend: boolean
  /** Áp dụng Quy tắc an toàn chung của Thư mục dùng chung. */
  useSharedRules: boolean
  suggestRelated: boolean
  memory: { enabled: boolean; rounds: number | null }
  tone: { preset: number | null; custom: string }
  answerLength: { preset: number | null; custom: string }
  humanLike: {
    enabled: boolean
    /** Số tin tách tối đa 1..10 (1 = không tách). */
    maxSplit: number
    /** Khoảng cách gửi giữa các tin tách (giây, 0..60). */
    bubbleDelay: { min: number; max: number }
    /** Độ trễ phản hồi trước tin đầu (giây, 0..60). `enabled` giữ cho dữ liệu cũ. */
    replyDelay: { enabled: boolean; min: number; max: number }
    /** Trả lời kỹ lưỡng: câu chờ trước lượt cần tra cứu (*** tách nhiều câu). */
    thorough: { enabled: boolean; phrases: string }
  }
  autoStop: AutoStopSettings
  unsupportedReply: { enabled: boolean; text: string }
  groupReply: { enabled: boolean; mode: GroupReplyMode; onMention: boolean; onKeywords: boolean; keywords: string[]; ignoreMemberNames: string[] }
  currentTime: { enabled: boolean; timezone: string }
  handoff: {
    enabled: boolean
    timeWindows: TimeWindow[]
    notifyMessage: string | null
    intents: HandoffIntent[]
    attributeRules: Condition[]
    /** Chuyển cho nhân viên khi "Tự động ngừng trả lời" khớp điều kiện dừng. */
    onAutoStop: boolean
    /**
     * Từ khoá chuyển người: tin khách chứa từ khoá → chuyển ngay, không gọi AI. "/mẫu/" = biểu thức chính quy.
     * Tuỳ chọn trong kiểu (withDefaults luôn điền []) để chỗ dựng settings riêng không bắt buộc khai báo.
     */
    keywords?: string[]
  }
  collectAttributes: CollectAttribute[]
  autoTag: { enabled: boolean; tags: string[] }
  appointmentEnabled: boolean
  /** Học thông tin quảng cáo — sắp có (chưa có nguồn quảng cáo), chỉ lưu cờ. */
  adLearning: { enabled: boolean }
}

export const DEFAULT_HANDOFF_INTENT: HandoffIntent = {
  id: 'ask-human',
  name: 'Khách yêu cầu gặp người thật', // i18n-ignore (dữ liệu: tên ý định lưu vào bot)
  description: 'Khách nói rõ muốn nói chuyện với nhân viên / người thật / quản lý. Không chuyển khi khách chỉ hỏi "có ai không" để chào.', // i18n-ignore (dữ liệu: ý định lưu vào bot, AI đọc)
  enabled: true,
}

export function defaultBotSettings(): BotSettings {
  return {
    outputLanguage: 'auto',
    fastMode: false,
    unknownReply: '',
    retrievalThreshold: null,
    rewriteQuery: false,
    debounceSeconds: 5,
    verifyBeforeSend: false,
    useSharedRules: true,
    suggestRelated: false,
    memory: { enabled: true, rounds: null },
    tone: { preset: null, custom: '' },
    answerLength: { preset: null, custom: '' },
    humanLike: { enabled: false, maxSplit: 3, bubbleDelay: { min: 1, max: 3 }, replyDelay: { enabled: false, min: 2, max: 4 }, thorough: { enabled: false, phrases: '' } },
    autoStop: { enabled: false, conditionGroups: [], schedules: [], closingMessage: '', closingImages: [] },
    unsupportedReply: { enabled: false, text: '' },
    groupReply: { enabled: true, mode: 'all', onMention: true, onKeywords: false, keywords: [], ignoreMemberNames: [] },
    currentTime: { enabled: false, timezone: 'Asia/Ho_Chi_Minh' },
    handoff: { enabled: true, timeWindows: [], notifyMessage: null, intents: [{ ...DEFAULT_HANDOFF_INTENT }], attributeRules: [], onAutoStop: false, keywords: [] },
    collectAttributes: [],
    autoTag: { enabled: false, tags: [] },
    appointmentEnabled: true,
    adLearning: { enabled: false },
  }
}

/**
 * autoStop từ server (có thể là dạng cũ { conditions[], serviceTime[] }) → dạng mới. Dạng cũ khớp MỘT điều kiện
 * → mỗi điều kiện một nhóm (HOẶC). serviceTime cũ ("chỉ xét trong khung giờ") ngược nghĩa khung dừng → bỏ.
 */
export function autoStopWithDefaults(raw: unknown): AutoStopSettings {
  const a = (raw && typeof raw === 'object' ? raw : {}) as Partial<AutoStopSettings> & { conditions?: Condition[] }
  const groups = Array.isArray(a.conditionGroups)
    ? a.conditionGroups.map((g) => ({ conditions: Array.isArray(g?.conditions) ? g.conditions : [] }))
    : Array.isArray(a.conditions) ? a.conditions.map((c) => ({ conditions: [c] })) : []
  return {
    enabled: !!a.enabled,
    conditionGroups: groups,
    schedules: Array.isArray(a.schedules) ? a.schedules.map((x) => ({ ...x, days: Array.isArray(x.days) ? x.days : [] })) : [],
    closingMessage: typeof a.closingMessage === 'string' ? a.closingMessage : '',
    closingImages: Array.isArray(a.closingImages) ? a.closingImages.filter((u): u is string => typeof u === 'string') : [],
  }
}

/** 'inherit' (dữ liệu cũ chưa chuyển đổi) → 'all' — máy chủ không còn nhận 'inherit' khi lưu. */
function groupReplyWithDefaults(d: BotSettings['groupReply'], raw: Partial<BotSettings['groupReply']> | undefined): BotSettings['groupReply'] {
  const g = { ...d, ...raw }
  return { ...g, mode: g.mode === 'trigger' ? 'trigger' : 'all' }
}

/** Gộp settings từ server (có thể thiếu trường với server cũ) vào mặc định. */
export function withDefaults(raw: Partial<BotSettings> | null | undefined): BotSettings {
  const d = defaultBotSettings()
  if (!raw) return d
  return {
    ...d,
    ...raw,
    memory: { ...d.memory, ...raw.memory },
    tone: { ...d.tone, ...raw.tone },
    answerLength: { ...d.answerLength, ...raw.answerLength },
    humanLike: {
      ...d.humanLike, ...raw.humanLike,
      bubbleDelay: { ...d.humanLike.bubbleDelay, ...raw.humanLike?.bubbleDelay },
      replyDelay: { ...d.humanLike.replyDelay, ...raw.humanLike?.replyDelay },
      thorough: { ...d.humanLike.thorough, ...raw.humanLike?.thorough },
    },
    autoStop: autoStopWithDefaults(raw.autoStop),
    unsupportedReply: { ...d.unsupportedReply, ...raw.unsupportedReply },
    groupReply: groupReplyWithDefaults(d.groupReply, raw.groupReply),
    currentTime: { ...d.currentTime, ...raw.currentTime },
    handoff: { ...d.handoff, ...raw.handoff, keywords: Array.isArray(raw.handoff?.keywords) ? raw.handoff.keywords : [] },
    autoTag: { ...d.autoTag, ...raw.autoTag },
    adLearning: { ...d.adLearning, ...raw.adLearning },
    collectAttributes: raw.collectAttributes ?? [],
  }
}

export const PERSONA_MAX_CHARS = 20_000
/** Đợi khách nhắn xong tối đa (giây) — khớp backend MAX_DEBOUNCE_S. */
export const MAX_DEBOUNCE_S = 120

export function personaCounter(len: number): { text: string; over: boolean; near: boolean } {
  return {
    text: `${len.toLocaleString(currentLocale())} / ${PERSONA_MAX_CHARS.toLocaleString(currentLocale())}`,
    over: len > PERSONA_MAX_CHARS,
    near: len > PERSONA_MAX_CHARS * 0.9 && len <= PERSONA_MAX_CHARS,
  }
}

/** Bỏ dấu → khoá máy cho thuộc tính (khớp backend slugKey). */
export function slugKey(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D') // i18n-ignore
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'thuoc_tinh'
}

/** Khoá không trùng trong danh sách. */
export function uniqueKey(base: string, taken: string[]): string {
  let k = base
  let i = 2
  while (taken.includes(k)) k = `${base.slice(0, 36)}_${i++}`
  return k
}

/** Chèn token vào văn bản tại vị trí con trỏ; trả về văn bản mới + vị trí con trỏ sau token. */
export function insertAtCursor(text: string, token: string, start: number | null | undefined, end?: number | null): { text: string; cursor: number } {
  const s = start == null || start < 0 || start > text.length ? text.length : start
  const e = end == null || end < s || end > text.length ? s : end
  const next = text.slice(0, s) + token + text.slice(e)
  return { text: next, cursor: s + token.length }
}

/** Các trường thay đổi giữa hai object (so sâu bằng JSON) — dùng cho tự lưu chỉ gửi phần đổi. */
export function changedFields<T extends Record<string, unknown>>(prev: T, next: T): Partial<T> {
  const out: Partial<T> = {}
  for (const k of Object.keys(next) as Array<keyof T>) {
    if (JSON.stringify(prev[k] ?? null) !== JSON.stringify(next[k] ?? null)) out[k] = next[k]
  }
  return out
}

export const WEEKDAYS: Array<{ value: number; short: string }> = [
  { value: 1, get short() { return i18n.t('aiTrainLib:botSettings.weekday.d1') } }, { value: 2, get short() { return i18n.t('aiTrainLib:botSettings.weekday.d2') } }, { value: 3, get short() { return i18n.t('aiTrainLib:botSettings.weekday.d3') } }, { value: 4, get short() { return i18n.t('aiTrainLib:botSettings.weekday.d4') } },
  { value: 5, get short() { return i18n.t('aiTrainLib:botSettings.weekday.d5') } }, { value: 6, get short() { return i18n.t('aiTrainLib:botSettings.weekday.d6') } }, { value: 0, get short() { return i18n.t('aiTrainLib:botSettings.weekday.d0') } },
]

export const TIMEZONES: Array<{ value: string; label: string }> = [
  { value: 'Asia/Ho_Chi_Minh', get label() { return i18n.t('aiTrainLib:botSettings.timezone.vn') } },
  { value: 'Asia/Bangkok', label: 'Bangkok (GMT+7)' },
  { value: 'Asia/Singapore', label: 'Singapore (GMT+8)' },
  { value: 'Asia/Shanghai', get label() { return i18n.t('aiTrainLib:botSettings.timezone.cn') } },
  { value: 'Asia/Tokyo', get label() { return i18n.t('aiTrainLib:botSettings.timezone.jp') } },
  { value: 'Asia/Seoul', get label() { return i18n.t('aiTrainLib:botSettings.timezone.kr') } },
  { value: 'Australia/Sydney', label: 'Sydney' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'America/New_York', label: 'New York' },
  { value: 'America/Los_Angeles', label: 'Los Angeles' },
  { value: 'UTC', label: 'UTC' },
]

/** Trường khách AI có thể đồng bộ (ngoài thuộc tính tuỳ chỉnh "prop:<id>"). */
export const SYNC_PROFILE_FIELDS: Array<{ value: string; label: string }> = [
  { value: 'phone', get label() { return i18n.t('aiTrainLib:botSettings.syncField.phone') } },
  { value: 'email', label: 'Email' },
  { value: 'fullName', get label() { return i18n.t('aiTrainLib:botSettings.syncField.fullName') } },
  { value: 'jobTitle', get label() { return i18n.t('aiTrainLib:botSettings.syncField.jobTitle') } },
  { value: 'notes', get label() { return i18n.t('aiTrainLib:botSettings.syncField.notes') } },
]

/** Mô tả ngắn khung giờ (hiển thị). */
export function describeWindow(w: HandoffTimeWindow): string {
  const days = WEEKDAYS.filter((d) => w.days.includes(d.value)).map((d) => d.short).join(', ')
  return `${days || '—'} · ${w.start}–${w.end}`
}

/** Điều kiện còn thiếu gì (null = hợp lệ). */
export function conditionProblem(c: Condition): string | null {
  if (!c.variable) return i18n.t('aiTrainLib:botSettings.problem.condVar')
  if (c.op === 'is_null' || c.op === 'is_not_null') return null
  if (c.op === 'in' || c.op === 'not_in') return Array.isArray(c.value) && c.value.length ? null : i18n.t('aiTrainLib:botSettings.problem.condMulti')
  if (c.op === '>' || c.op === '<' || c.op === '>=' || c.op === '<=') {
    return c.value !== '' && c.value != null && Number.isFinite(Number(c.value)) ? null : i18n.t('aiTrainLib:botSettings.problem.condNumber')
  }
  return c.value !== '' && c.value != null ? null : i18n.t('aiTrainLib:botSettings.problem.condValue')
}

/** Kiểm nhanh phía giao diện (để báo trước khi tự lưu bị server từ chối). */
export function settingsProblems(s: BotSettings): string[] {
  const out: string[] = []
  const g = s.groupReply
  if (g.mode === 'trigger' && !g.onMention && !g.onKeywords) out.push(i18n.t('aiTrainLib:botSettings.settings.groupTrigger'))
  if (g.mode === 'trigger' && g.onKeywords && g.keywords.length === 0) out.push(i18n.t('aiTrainLib:botSettings.settings.groupKeywords'))
  const stopConds = s.autoStop.conditionGroups.flatMap((g) => g.conditions)
  if (s.autoStop.enabled && stopConds.length === 0 && s.autoStop.schedules.length === 0) out.push(i18n.t('aiTrainLib:botSettings.settings.autoStopEmpty'))
  for (const c of stopConds) { const p = conditionProblem(c); if (p) out.push(i18n.t('aiTrainLib:botSettings.settings.autoStopCondition', { problem: p })) }
  for (const w of s.autoStop.schedules) {
    if (w.repeat === 'weekly' && !w.days.length) out.push(i18n.t('aiTrainLib:botSettings.settings.autoStopWeekday'))
    if (!w.start || !w.end) out.push(i18n.t('aiTrainLib:botSettings.settings.autoStopTimes'))
  }
  for (const c of s.handoff.attributeRules) { const p = conditionProblem(c); if (p) out.push(i18n.t('aiTrainLib:botSettings.settings.handoffAttribute', { problem: p })) }
  if (s.unsupportedReply.enabled && !s.unsupportedReply.text.trim()) out.push(i18n.t('aiTrainLib:botSettings.settings.unsupportedReply'))
  const hl = s.humanLike
  for (const p of humanLikeProblems(hl)) out.push(i18n.t('aiTrainLib:botSettings.settings.humanLike', { problem: p }))
  if (!Number.isInteger(s.debounceSeconds) || s.debounceSeconds < 0 || s.debounceSeconds > MAX_DEBOUNCE_S) out.push(i18n.t('aiTrainLib:botSettings.settings.debounce', { max: MAX_DEBOUNCE_S }))
  if ((s.handoff.keywords ?? []).length > 50) out.push(i18n.t('aiTrainLib:botSettings.settings.handoffKeywords'))
  if (s.memory.rounds != null && (s.memory.rounds < 1 || s.memory.rounds > 50)) out.push(i18n.t('aiTrainLib:botSettings.settings.memory'))
  for (const w of s.handoff.timeWindows) {
    if (!w.days.length) out.push(i18n.t('aiTrainLib:botSettings.settings.windowDay'))
  }
  for (const i of s.handoff.intents) if (!i.name.trim()) out.push(i18n.t('aiTrainLib:botSettings.settings.intentName'))
  for (const a of s.collectAttributes) {
    if (a.type === 'select' && !(a.options ?? []).length) out.push(i18n.t('aiTrainLib:botSettings.settings.attributeOption', { name: a.name }))
  }
  return [...new Set(out)]
}

/** Giọng điệu dựng sẵn (1..5) — khớp backend TONE_PRESETS. */
export const TONE_PRESET_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, get label() { return i18n.t('aiTrainLib:botSettings.tone.friendly') } }, { value: 2, get label() { return i18n.t('aiTrainLib:botSettings.tone.natural') } }, { value: 3, get label() { return i18n.t('aiTrainLib:botSettings.tone.gentle') } },
  { value: 4, get label() { return i18n.t('aiTrainLib:botSettings.tone.professional') } }, { value: 5, get label() { return i18n.t('aiTrainLib:botSettings.tone.humorous') } },
]
/** Độ dài câu trả lời dựng sẵn (1..3). */
export const LENGTH_PRESET_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, get label() { return i18n.t('aiTrainLib:botSettings.length.short') } }, { value: 2, get label() { return i18n.t('aiTrainLib:botSettings.length.standard') } }, { value: 3, get label() { return i18n.t('aiTrainLib:botSettings.length.detailed') } },
]

/** "AI giống người thật": giới hạn (khớp backend MAX_SPLIT / MAX_DELAY_S). */
export const HL_MAX_SPLIT = 10
export const HL_MAX_DELAY = 60
export const HL_THOROUGH_MAX = 1000

export type HumanLike = BotSettings['humanLike']
/** Lỗi từng ô của hộp "Cài đặt AI giống người thật" (khoá = tên ô) — hàm thuần, có test. */
export function humanLikeFieldErrors(h: HumanLike): Partial<Record<'replyDelay' | 'maxSplit' | 'bubbleDelay' | 'thorough', string>> {
  const out: Partial<Record<'replyDelay' | 'maxSplit' | 'bubbleDelay' | 'thorough', string>> = {}
  const range = (r: { min: number; max: number }) => {
    if (![r.min, r.max].every((n) => Number.isInteger(n) && n >= 0 && n <= HL_MAX_DELAY)) return i18n.t('aiTrainLib:botSettings.humanLike.delayRange', { max: HL_MAX_DELAY })
    if (r.min > r.max) return i18n.t('aiTrainLib:botSettings.humanLike.minMax')
    return undefined
  }
  const rd = range(h.replyDelay); if (rd) out.replyDelay = rd
  if (!Number.isInteger(h.maxSplit) || h.maxSplit < 1 || h.maxSplit > HL_MAX_SPLIT) out.maxSplit = i18n.t('aiTrainLib:botSettings.humanLike.maxSplit', { max: HL_MAX_SPLIT })
  const bd = range(h.bubbleDelay); if (bd) out.bubbleDelay = bd
  if (h.thorough.phrases.length > HL_THOROUGH_MAX) out.thorough = i18n.t('aiTrainLib:botSettings.humanLike.thorough', { max: HL_THOROUGH_MAX })
  return out
}
export function humanLikeProblems(h: HumanLike): string[] {
  const e = humanLikeFieldErrors(h)
  return [
    e.replyDelay && i18n.t('aiTrainLib:botSettings.humanLike.p.replyDelay', { error: e.replyDelay }),
    e.maxSplit && i18n.t('aiTrainLib:botSettings.humanLike.p.maxSplit', { error: e.maxSplit }),
    e.bubbleDelay && i18n.t('aiTrainLib:botSettings.humanLike.p.bubbleDelay', { error: e.bubbleDelay }),
    e.thorough && i18n.t('aiTrainLib:botSettings.humanLike.p.thorough', { error: e.thorough }),
  ].filter((x): x is string => !!x)
}
